import React, { useEffect, useState } from 'react';
import { CreditCard, Zap, Shield, ArrowUpCircle, Clock, BarChart3, AlertTriangle, ExternalLink, FileText } from 'lucide-react';
import { CompanyBilling, PlanUsage, PlanName } from '../types';

interface BillingViewProps {
  billing: CompanyBilling | null;
  usage: PlanUsage | null;
  trialDaysLeft: number | null;
  onOpenCheckout: (plan: 'starter' | 'pro' | 'business', billing: 'monthly' | 'annual') => void;
  onSetSpendingCap: (cap: number) => Promise<void>;
  loading: boolean;
  onOpenPortal?: () => void;
}

const PLAN_DETAILS: Record<string, { label: string; color: string; tools: number; ai: number; price: number }> = {
  trial: { label: 'Trial', color: 'bg-gray-100 text-gray-700', tools: 25, ai: 5, price: 0 },
  tester: { label: 'Tester', color: 'bg-indigo-100 text-indigo-700', tools: 0, ai: 0, price: 0 },
  starter: { label: 'Starter', color: 'bg-blue-100 text-blue-700', tools: 25, ai: 10, price: 29 },
  pro: { label: 'Pro', color: 'bg-purple-100 text-purple-700', tools: 60, ai: 30, price: 69 },
  business: { label: 'Business', color: 'bg-amber-100 text-amber-700', tools: 150, ai: 100, price: 139 },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', tools: 0, ai: 0, price: 0 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', tools: 0, ai: 0, price: 0 },
};

const UPGRADE_OPTIONS: { plan: 'starter' | 'pro' | 'business'; label: string; price: number; annualPrice: number; tools: number; ai: number; features: string[] }[] = [
  { plan: 'starter', label: 'Starter', price: 29, annualPrice: 24, tools: 25, ai: 10, features: ['25 tools', '10 AI queries/mo', 'Email support (72h)'] },
  { plan: 'pro', label: 'Pro', price: 69, annualPrice: 55, tools: 60, ai: 30, features: ['60 tools', '30 AI queries/mo', 'SaaS Discovery', 'Approval workflows', '1-year audit log'] },
  { plan: 'business', label: 'Business', price: 139, annualPrice: 111, tools: 150, ai: 100, features: ['150 tools', '100 AI queries/mo', 'Multi-level approvals', 'Cost trends', 'Free-form AI', 'API access'] },
];

const BillingView: React.FC<BillingViewProps> = ({ billing, usage, trialDaysLeft, onOpenCheckout, onSetSpendingCap, loading, onOpenPortal }) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [editCap, setEditCap] = useState(false);
  const [capValue, setCapValue] = useState(billing?.aiSpendingCap ?? 15);
  const [savingCap, setSavingCap] = useState(false);

  if (loading || !billing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const plan = PLAN_DETAILS[billing.plan] ?? PLAN_DETAILS.trial;
  const isActive = billing.plan !== 'expired' && billing.plan !== 'cancelled';
  const hasSubscription = !!billing.paddleSubscriptionId;
  const currentPlanOrder = ['trial', 'tester', 'expired', 'cancelled', 'starter', 'pro', 'business'].indexOf(billing.plan);

  const handleSaveCap = async () => {
    const normalizedCap = Math.max(0, Number(capValue) || 0);
    setSavingCap(true);
    await onSetSpendingCap(normalizedCap);
    setEditCap(false);
    setSavingCap(false);
  };

  useEffect(() => {
    setCapValue(billing.aiSpendingCap ?? 15);
  }, [billing.aiSpendingCap]);

  const capPercent = billing.aiSpendingCap > 0
    ? Math.min(100, (usage?.aiOverageAmount ?? 0) / billing.aiSpendingCap * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Trial Banner */}
      {billing.plan === 'trial' && trialDaysLeft !== null && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-800">
              {trialDaysLeft > 0
                ? `${trialDaysLeft} day${trialDaysLeft > 1 ? 's' : ''} left in your trial`
                : 'Your trial has expired'}
            </p>
            <p className="text-sm text-blue-600 mt-1">
              Subscribe to keep managing your tools and using the Oasix Advisor.
            </p>
          </div>
        </div>
      )}

      {/* Expired / Cancelled Banner */}
      {(billing.plan === 'expired' || billing.plan === 'cancelled') && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800">
              {billing.plan === 'expired' ? 'Your trial has expired' : 'Your subscription was cancelled'}
            </p>
            <p className="text-sm text-red-600 mt-1">
              Your data is preserved for 90 days. Subscribe to continue.
            </p>
          </div>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-500" />
            Current Plan
          </h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${plan.color}`}>
            {plan.label}
          </span>
        </div>

        {isActive && billing.planLimits && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-800">{billing.planLimits.max_tools}</p>
              <p className="text-xs text-gray-500">Tools limit</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-800">{billing.planLimits.ai_queries_included}</p>
              <p className="text-xs text-gray-500">AI queries/mo</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-800">Unlimited</p>
              <p className="text-xs text-gray-500">Employees</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-800">
                {billing.planLimits.ai_overage_rate > 0 ? `€${billing.planLimits.ai_overage_rate}` : '—'}
              </p>
              <p className="text-xs text-gray-500">AI overage/query</p>
            </div>
          </div>
        )}
      </div>

      {/* Manage Subscription (active subscribers only) */}
      {hasSubscription && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-3">
            <CreditCard className="w-5 h-5 text-gray-500" />
            Manage Subscription
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Update your payment method, view invoices, or cancel your subscription via the Paddle customer portal.
          </p>
          <div className="flex flex-wrap gap-3">
            {onOpenPortal && (
              <button
                onClick={onOpenPortal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg text-sm transition-colors"
              >
                <FileText className="w-4 h-4" />
                Invoices & Payment Method
                <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
              </button>
            )}
            {!onOpenPortal && (
              <span className="text-sm text-gray-500">
                Customer portal link will appear after your next successful billing sync.
              </span>
            )}
            {billing.paddleSubscriptionStatus === 'scheduled_cancel' && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4" />
                Cancellation scheduled — active until end of billing period
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage */}
      {usage && hasSubscription && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-gray-500" />
            Current Period Usage
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Tools</span>
                <span className="font-medium">{usage.toolsCount}/{billing.planLimits.max_tools}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (usage.toolsCount / billing.planLimits.max_tools) * 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">AI Queries</span>
                <span className="font-medium">{usage.aiQueriesUsed}/{billing.planLimits.ai_queries_included}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (usage.aiQueriesUsed / Math.max(1, billing.planLimits.ai_queries_included)) * 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">AI Overage</span>
                <span className="font-medium">€{usage.aiOverageAmount.toFixed(2)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className={`h-2 rounded-full transition-all ${capPercent >= 100 ? 'bg-red-500' : capPercent >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${capPercent}%` }}
                />
              </div>
              <div className="flex items-center gap-2 mt-1">
                {!editCap ? (
                  <>
                    <span className="text-xs text-gray-500">Cap: €{billing.aiSpendingCap}/mo</span>
                    <button onClick={() => { setCapValue(billing.aiSpendingCap); setEditCap(true); }} className="text-xs text-blue-600 hover:underline">Edit</button>
                  </>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">€</span>
                    <input
                      type="number"
                      value={capValue}
                      onChange={e => setCapValue(Number(e.target.value))}
                      className="w-16 text-xs border rounded px-1 py-0.5"
                      min={0}
                      step={5}
                    />
                    <button onClick={handleSaveCap} disabled={savingCap} className="text-xs text-green-600 hover:underline disabled:opacity-60">{savingCap ? 'Saving...' : 'Save'}</button>
                    <button onClick={() => setEditCap(false)} className="text-xs text-gray-400 hover:underline">Cancel</button>
                  </div>
                )}
              </div>
              {capPercent >= 80 && (
                <p className={`mt-1 text-xs ${capPercent >= 100 ? 'text-red-600' : 'text-amber-600'}`}>
                  {capPercent >= 100 ? 'Spending cap reached. AI overage will be blocked until cap is increased.' : 'You reached 80% of your monthly AI spending cap.'}
                </p>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Period: {usage.periodStart} to {usage.periodEnd}
          </p>
        </div>
      )}

      {/* Plan Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-gray-500" />
            {hasSubscription ? 'Change Plan' : 'Subscribe'}
          </h3>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${billingCycle === 'monthly' ? 'bg-white shadow text-gray-800 font-medium' : 'text-gray-500'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${billingCycle === 'annual' ? 'bg-white shadow text-gray-800 font-medium' : 'text-gray-500'}`}
            >
              Annual <span className="text-green-600 text-xs font-medium">-20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {UPGRADE_OPTIONS.map(opt => {
            const optOrder = ['starter', 'pro', 'business'].indexOf(opt.plan) + 3;
            const isCurrent = billing.plan === opt.plan;
            const isDowngrade = optOrder < currentPlanOrder;
            const price = billingCycle === 'annual' ? opt.annualPrice : opt.price;

            return (
              <div
                key={opt.plan}
                className={`border rounded-xl p-5 flex flex-col ${isCurrent ? 'border-blue-400 bg-blue-50/30 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-800">{opt.label}</h4>
                  {isCurrent && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Current</span>}
                </div>
                <div className="mb-3">
                  {billingCycle === 'annual' ? (
                    <>
                      <span className="text-lg text-gray-400 line-through decoration-green-500 decoration-2">€{opt.price}</span>
                      <span className="text-3xl font-bold text-gray-900 ml-2">€{opt.annualPrice}</span>
                      <span className="text-gray-500 text-sm">/mo</span>
                      <p className="text-xs text-green-600 mt-0.5">Billed €{opt.annualPrice * 12}/year</p>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-gray-900">€{opt.price}</span>
                      <span className="text-gray-500 text-sm">/mo</span>
                    </>
                  )}
                </div>
                <ul className="text-sm text-gray-600 space-y-1.5 mb-4 flex-1">
                  {opt.features.map(f => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                  <li className="flex items-start gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                    Unlimited employees
                  </li>
                </ul>
                {!isCurrent && (
                  <button
                    onClick={() => onOpenCheckout(opt.plan, billingCycle)}
                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      isDowngrade
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {isDowngrade ? 'Downgrade' : hasSubscription ? 'Upgrade' : 'Subscribe'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center">
          All plans include unlimited employees. Payments processed securely by Paddle.
          {hasSubscription && ' Manage your subscription, invoices, and payment method via the Paddle customer portal.'}
        </p>
      </div>
    </div>
  );
};

export default BillingView;
